// Regras puras da listagem de pedidos de compra — testáveis sem renderizar.

/** Páginas a exibir, com reticências: 1 … 5 6 7 … 12 (mesmo desenho da listagem de pedidos de venda). */
export function getPageRange(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export type QuickNavOutcome = { kind: 'open'; id: number } | { kind: 'ambiguous'; ids: number[] };

/**
 * O que fazer com o resultado do "Nº do pedido". Um pedido → abre. Mais de um → a tela filtra a
 * listagem por esse número para a pessoa escolher: o número exibido é o OrderNumber do Bling ou,
 * sem ele, o id, e as duas faixas podem se cruzar (hoje o 322 já está em dois pedidos migrados).
 * Nenhum → null.
 */
export function quickNavOutcome(ids: number[]): QuickNavOutcome | null {
  if (ids.length === 0) return null;
  if (ids.length === 1) return { kind: 'open', id: ids[0] };
  return { kind: 'ambiguous', ids };
}

// ─── Ordenação ──────────────────────────────────────────────────

export type PurchaseOrderSort = 'padrao' | 'criacao_desc' | 'criacao_asc';

export interface PurchaseOrderSortOption {
  value: PurchaseOrderSort;
  label: string;
  /** Explicação mostrada na tela quando a opção está ativa. */
  hint: string;
}

export const PURCHASE_ORDER_SORT_OPTIONS: PurchaseOrderSortOption[] = [
  {
    value: 'padrao',
    label: 'Padrão (prioritários primeiro)',
    hint:
      'Pedidos marcados como PRIORITÁRIO ficam no topo. Os demais seguem a data do pedido, da mais recente para a mais antiga; no mesmo dia, o cadastrado por último vem primeiro.',
  },
  {
    value: 'criacao_desc',
    label: 'Data de criação — mais recente primeiro',
    hint:
      'Todos os pedidos pela data e hora em que foram cadastrados no ERP, do mais novo para o mais antigo. Os prioritários não ficam fixos no topo (continuam com o selo PRIORITÁRIO).',
  },
  {
    value: 'criacao_asc',
    label: 'Data de criação — mais antigo primeiro',
    hint:
      'Todos os pedidos pela data e hora em que foram cadastrados no ERP, do mais antigo para o mais novo. Os prioritários não ficam fixos no topo (continuam com o selo PRIORITÁRIO).',
  },
];

export const DEFAULT_PURCHASE_ORDER_SORT: PurchaseOrderSort = 'padrao';

/** Chave do localStorage onde a escolha fica lembrada neste navegador. */
export const PURCHASE_ORDER_SORT_STORAGE_KEY = 'erp.purchaseOrders.sort';

/** Valor salvo (ou vindo do select) → opção válida; qualquer outra coisa vira o padrão. */
export function parsePurchaseOrderSort(value: string | null | undefined): PurchaseOrderSort {
  const found = PURCHASE_ORDER_SORT_OPTIONS.find(o => o.value === value);
  return found ? found.value : DEFAULT_PURCHASE_ORDER_SORT;
}

/** Parâmetros da API para a opção escolhida. */
export function purchaseOrderSortParams(sort: PurchaseOrderSort): {
  orderBy: string;
  orderDirection: 'asc' | 'desc';
  priority_first: boolean;
} {
  switch (sort) {
    case 'criacao_desc':
      return { orderBy: 'created_at', orderDirection: 'desc', priority_first: false };
    case 'criacao_asc':
      return { orderBy: 'created_at', orderDirection: 'asc', priority_first: false };
    default:
      return { orderBy: 'order_date', orderDirection: 'desc', priority_first: true };
  }
}

/** Explicação da ordenação ativa. */
export function purchaseOrderSortHint(sort: PurchaseOrderSort): string {
  return (PURCHASE_ORDER_SORT_OPTIONS.find(o => o.value === sort) ?? PURCHASE_ORDER_SORT_OPTIONS[0]).hint;
}

/** A busca é só um número de pedido ("76", "#76")? Nesse caso o número exato vem antes. */
export const isOrderNumberSearch = (term: string): boolean => /^#?\s*\d+$/.test(term.trim());
